import { useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, Car, User, Phone, Mail, FileText, Loader2, ArrowLeft, CheckCircle, Lightbulb } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

export function HeadlightRestorationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get('cancelled') === 'true';
  
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    preferred_date: "",
    preferred_time: "",
    vehicle_make: "",
    vehicle_model: "",
    vehicle_year: "",
    service_type: "headlights",
    notes: ""
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const required = ['name', 'email', 'phone', 'preferred_date', 'preferred_time', 'vehicle_make', 'vehicle_model', 'vehicle_year'];
    for (const field of required) {
      if (!form[field]) {
        toast.error(`Please fill in ${field.replace('_', ' ')}`);
        return;
      }
    }
    
    setLoading(true);
    try {
      // Pass origin_url for dynamic redirect URLs
      const payload = {
        ...form,
        origin_url: window.location.origin
      };
      const res = await axios.post(`${API}/api/stripe/create-headlight-deposit-session`, payload);
      // Redirect to Stripe checkout
      window.location.href = res.data.checkout_url;
    } catch (err) {
      console.error("Booking error:", err);
      toast.error(err.response?.data?.detail || "Failed to create booking. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-28 pb-20" data-testid="headlight-restoration-page">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        {/* Back button */}
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm tracking-[0.15em] uppercase font-body transition-colors bg-transparent border-none cursor-pointer mb-8"
        >
          <ArrowLeft size={16} /> Back to Site
        </button>

        {/* Header */}
        <motion.div {...fadeUp} className="mb-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#3b82f6]/10 border border-[#3b82f6]/30 mb-6">
            <Lightbulb size={28} className="text-[#3b82f6]" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-tight text-white mb-4">
            Headlight<br />Restoration
          </h1>
          <p className="text-zinc-400 font-body text-base max-w-lg mx-auto">
            Restore your cloudy, yellowed or oxidised headlights to crystal clear. Professional sanding and re-clearing.
          </p>
        </motion.div>

        {/* Price card */}
        <motion.div 
          {...fadeUp}
          transition={{ delay: 0.1 }}
          className="bg-[#09090b] border border-white/5 p-6 mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="font-heading text-xl uppercase tracking-wide text-white mb-2">Service Price</h3>
              <p className="text-zinc-500 font-body text-sm">Headlights or Taillights (per pair)</p>
            </div>
            <div className="text-right">
              <p className="font-heading text-4xl text-white">$250</p>
              <p className="text-[#3b82f6] text-sm font-body">$50 deposit to book</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-white/5 grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2 text-zinc-400">
              <CheckCircle size={14} className="text-[#22c55e]" />
              <span>Professional wet-sanding</span>
            </div>
            <div className="flex items-center gap-2 text-zinc-400">
              <CheckCircle size={14} className="text-[#22c55e]" />
              <span>UV protective clear coat</span>
            </div>
            <div className="flex items-center gap-2 text-zinc-400">
              <CheckCircle size={14} className="text-[#22c55e]" />
              <span>Crystal clear finish</span>
            </div>
            <div className="flex items-center gap-2 text-zinc-400">
              <CheckCircle size={14} className="text-[#22c55e]" />
              <span>24hr refund policy</span>
            </div>
          </div>
        </motion.div>

        {cancelled && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 mb-6 text-center">
            <p className="text-yellow-400 text-sm">Payment was cancelled. You can try again below.</p>
          </div>
        )}

        {/* Booking form */}
        <motion.form 
          {...fadeUp}
          transition={{ delay: 0.2 }}
          onSubmit={handleSubmit}
          className="bg-[#09090b] border border-white/5 p-6 md:p-8 space-y-6"
        >
          <h3 className="font-heading text-lg uppercase tracking-wide text-white mb-6">Your Details</h3>
          
          {/* Contact info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Full Name *"
                className="w-full bg-transparent border border-white/10 focus:border-[#3b82f6]/50 outline-none p-4 pl-11 text-white font-body placeholder-zinc-600 transition-colors"
                required
              />
            </div>
            <div className="relative">
              <Phone size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="Phone Number *"
                className="w-full bg-transparent border border-white/10 focus:border-[#3b82f6]/50 outline-none p-4 pl-11 text-white font-body placeholder-zinc-600 transition-colors"
                required
              />
            </div>
          </div>
          
          <div className="relative">
            <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Email Address *"
              className="w-full bg-transparent border border-white/10 focus:border-[#3b82f6]/50 outline-none p-4 pl-11 text-white font-body placeholder-zinc-600 transition-colors"
              required
            />
          </div>

          <h3 className="font-heading text-lg uppercase tracking-wide text-white pt-4">Vehicle Details</h3>
          
          {/* Vehicle info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Car size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input
                type="text"
                name="vehicle_make"
                value={form.vehicle_make}
                onChange={handleChange}
                placeholder="Make (e.g., Toyota) *"
                className="w-full bg-transparent border border-white/10 focus:border-[#3b82f6]/50 outline-none p-4 pl-11 text-white font-body placeholder-zinc-600 transition-colors"
                required
              />
            </div>
            <input
              type="text"
              name="vehicle_model"
              value={form.vehicle_model}
              onChange={handleChange}
              placeholder="Model (e.g., Supra) *"
              className="w-full bg-transparent border border-white/10 focus:border-[#3b82f6]/50 outline-none p-4 text-white font-body placeholder-zinc-600 transition-colors"
              required
            />
            <input
              type="text"
              name="vehicle_year"
              value={form.vehicle_year}
              onChange={handleChange}
              placeholder="Year *"
              className="w-full bg-transparent border border-white/10 focus:border-[#3b82f6]/50 outline-none p-4 text-white font-body placeholder-zinc-600 transition-colors"
              required
            />
          </div>

          <h3 className="font-heading text-lg uppercase tracking-wide text-white pt-4">Service Type</h3>
          
          {/* Service type selection */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { value: "headlights", label: "Headlights Only", price: "$250" },
              { value: "taillights", label: "Taillights Only", price: "$250" },
              { value: "both", label: "Both (Front & Rear)", price: "$450" }
            ].map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => setForm({ ...form, service_type: option.value })}
                className={`p-4 border text-left transition-colors cursor-pointer ${
                  form.service_type === option.value
                    ? "border-[#3b82f6] bg-[#3b82f6]/10"
                    : "border-white/10 hover:border-white/20"
                }`}
              >
                <p className="text-white font-body text-sm">{option.label}</p>
                <p className="text-[#3b82f6] font-heading text-lg mt-1">{option.price}</p>
              </button>
            ))}
          </div>

          <h3 className="font-heading text-lg uppercase tracking-wide text-white pt-4">Preferred Time</h3>
          
          {/* Date/Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Calendar size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input
                type="date"
                name="preferred_date"
                value={form.preferred_date}
                onChange={handleChange}
                className="w-full bg-transparent border border-white/10 focus:border-[#3b82f6]/50 outline-none p-4 pl-11 text-white font-body placeholder-zinc-600 transition-colors"
                required
              />
            </div>
            <div className="relative">
              <Clock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
              <select
                name="preferred_time"
                value={form.preferred_time}
                onChange={handleChange}
                className="w-full bg-[#09090b] border border-white/10 focus:border-[#3b82f6]/50 outline-none p-4 pl-11 text-white font-body transition-colors appearance-none cursor-pointer"
                required
              >
                <option value="">Select Time *</option>
                <option value="8:00 AM">8:00 AM</option>
                <option value="9:00 AM">9:00 AM</option>
                <option value="10:00 AM">10:00 AM</option>
                <option value="11:00 AM">11:00 AM</option>
                <option value="12:00 PM">12:00 PM</option>
                <option value="1:00 PM">1:00 PM</option>
                <option value="2:00 PM">2:00 PM</option>
                <option value="3:00 PM">3:00 PM</option>
                <option value="4:00 PM">4:00 PM</option>
              </select>
            </div>
          </div>

          {/* Notes */}
          <div className="relative">
            <FileText size={16} className="absolute left-4 top-4 text-zinc-600" />
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              placeholder="Any additional notes (optional)"
              rows={3}
              className="w-full bg-transparent border border-white/10 focus:border-[#3b82f6]/50 outline-none p-4 pl-11 text-white font-body placeholder-zinc-600 transition-colors resize-none"
            />
          </div>

          {/* Deposit info */}
          <div className="bg-[#3b82f6]/10 border border-[#3b82f6]/30 p-4">
            <p className="text-[#3b82f6] font-body text-sm">
              <strong>Deposit:</strong> Pay ${form.service_type === 'both' ? '100' : '50'} now to secure your booking. 
              Remaining ${form.service_type === 'both' ? '350' : '200'} due on completion.
            </p>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-[#3b82f6] text-white font-heading text-sm tracking-[0.15em] uppercase px-8 py-4 hover:bg-[#3b82f6]/80 transition-colors cursor-pointer border-none disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Pay ${form.service_type === 'both' ? '100' : '50'} Deposit & Book
              </>
            )}
          </button>
          
          <p className="text-zinc-600 text-xs text-center">
            Secure payment via Stripe. Refundable within 24 hours.
          </p>
        </motion.form>
      </div>
    </div>
  );
}

export default HeadlightRestorationPage;
